<template>
	<div>
		<!-- Pending Invitations -->
		<div v-if="activeTab === 'pending'">
			<div v-if="isDesktop">
				<div class="md-table-toolbar">
					<div class="flex justify-between">
						<md-field style="width: 30%">
							<label for="pages-pending">Per page</label>
							<md-select v-model="pagination.perPage" name="pages-pending">
								<md-option v-for="item in pagination.perPageOptions" :key="item" :label="item" :value="item">{{ item }}</md-option>
							</md-select>
						</md-field>

						<md-field style="width: 30%">
							<md-input type="search" class="mb-3" clearable style="width: 200px" placeholder="Search invitations" v-model="searchQueryModel" />
						</md-field>
					</div>
				</div>
				<md-table
					v-if="queriedData.length > 0"
					:value="queriedData"
					:md-sort.sync="currentSort"
					:md-sort-order.sync="currentSortOrder"
					:md-sort-fn="customSort"
					class="paginated-table table-striped table-hover"
				>
					<md-table-row slot="md-table-row" slot-scope="{ item }">
						<md-table-cell md-label="Company Name">{{ item.company }}</md-table-cell>
						<md-table-cell md-label="Job Title">{{ item.job }}</md-table-cell>
						<md-table-cell md-label="Status">{{ item.status }}</md-table-cell>
						<md-table-cell md-label="Invited Date">
							<md-icon class="md-icon-inline">event</md-icon>
							{{ item.date }}
						</md-table-cell>
						<md-table-cell md-label="Actions">
							<div style="display: flex; align-items: center; justify-content: center" v-if="!item.responseStatus">
								<md-button class="md-raised md-success" style="width: fit-content; padding: 0 !important" @click="$emit('open-accept-modal', item)">
									Accept
								</md-button>
								<md-button class="md-danger" style="margin: 0 1rem; width: fit-content; padding: 0 !important" @click="$emit('reject', item)"> Reject </md-button>
								<md-menu md-direction="bottom-start">
									<md-button class="md-icon-button" md-menu-trigger>
										<md-icon>more_vert</md-icon>
									</md-button>
									<md-menu-content>
										<md-menu-item @click="viewJobDetails(item.id)">
											<div class="flex items-center justify-center">
												<md-icon>visibility</md-icon>
												<span>View Job Details</span>
											</div>
										</md-menu-item>
									</md-menu-content>
								</md-menu>
							</div>
							<div v-else>
								<span class="confirmation">
									You have {{ item.responseStatus }} this invitation <span v-if="item.rejectionReason">: {{ item.rejectionReason }}</span
									>.
								</span>
							</div>
						</md-table-cell>
					</md-table-row>
				</md-table>
				<p v-else class="empty-message">You have no job invitations at the moment.</p>
			</div>

			<!-- Mobile Cards -->
			<div v-else>
				<div v-if="queriedData.length > 0">
					<md-card v-for="item in queriedData" :key="item.id" class="mb-3">
						<md-card-header>
							<div class="flex justify-between">
								<div class="md-title">{{ item.job }}</div>
								<md-button
									class="md-success flex items-center justify-center"
									style="padding: 0; height: 30px !important; min-width: 0 !important; width: 40px !important"
									@click="viewJobDetails(item.id)"
								>
									<md-icon>visibility</md-icon>
								</md-button>
							</div>
							<div class="md-subhead">{{ item.company }}</div>
						</md-card-header>
						<md-card-content>
							<p><strong>Status:</strong> {{ item.status }}</p>
							<p><strong>Date:</strong> {{ item.date }}</p>
							<p v-if="item.responseStatus">
								<strong>Response:</strong> {{ item.responseStatus }}
								<span v-if="item.rejectionReason"> - {{ item.rejectionReason }}</span>
							</p>
						</md-card-content>
						<md-card-actions v-if="!item.responseStatus">
							<md-button class="md-raised md-success" style="height: 30px !important; margin-right: 1rem" @click="$emit('open-accept-modal', item)">
								Accept
							</md-button>
							<md-button class="md-danger" style="height: 30px !important" @click="$emit('reject', item)"> Reject </md-button>
						</md-card-actions>
					</md-card>
				</div>
				<p v-else class="empty-message">You have no job invitations at the moment.</p>
			</div>
		</div>

		<!-- Previous Responses -->
		<div v-if="activeTab === 'previous'">
			<div v-if="isDesktop">
				<div class="md-table-toolbar">
					<div class="flex justify-between">
						<md-field style="width: 30%">
							<label for="pages-pending">Per page</label>
							<md-select v-model="pagination.perPage" name="pages-pending">
								<md-option v-for="item in pagination.perPageOptions" :key="item" :label="item" :value="item">{{ item }}</md-option>
							</md-select>
						</md-field>

						<md-field style="width: 30%">
							<md-input type="search" class="mb-3" clearable style="width: 200px" placeholder="Search invitations" v-model="searchQueryModel" />
						</md-field>
					</div>
				</div>
				<md-table
					v-if="queriedData.length > 0"
					:value="queriedData"
					:md-sort.sync="currentSort"
					:md-sort-order.sync="currentSortOrder"
					:md-sort-fn="customSort"
					class="paginated-table table-striped table-hover"
				>
					<md-table-row slot="md-table-row" slot-scope="{ item }">
						<md-table-cell md-label="Company Name">{{ item.company }}</md-table-cell>
						<md-table-cell md-label="Job Name">{{ item.job }}</md-table-cell>
						<md-table-cell md-label="Status">
							<span
								class="badge"
								:class="{
									accepted: item.responseStatus === 'Accepted',
									rejected: item.responseStatus === 'Rejected',
								}"
								>{{ item.responseStatus }}</span
							>
						</md-table-cell>
						<md-table-cell md-label="Reason for Rejection">
							{{ item.responseStatus === 'Rejected' ? item.rejectionReason || '-' : '—' }}
						</md-table-cell>
						<md-table-cell md-label="Invited Date">
							<md-icon class="md-icon-inline">event</md-icon>
							{{ item.date }}
						</md-table-cell>
						<md-table-cell md-label="Actions">
							<md-menu md-direction="bottom-start">
								<md-button class="md-icon-button" md-menu-trigger>
									<md-icon>more_vert</md-icon>
								</md-button>
								<md-menu-content>
									<md-menu-item @click="viewJobDetails(item.id)">
										<div class="flex items-center justify-center">
											<md-icon>visibility</md-icon>
											<span>View Job Details</span>
										</div>
									</md-menu-item>
								</md-menu-content>
							</md-menu>
						</md-table-cell>
					</md-table-row>
				</md-table>
				<p v-else class="empty-message">You haven’t responded to any invitations yet.</p>
			</div>

			<!-- Mobile Cards -->
			<div v-else>
				<div v-if="queriedData.length > 0">
					<md-card v-for="item in queriedData" :key="item.id" class="mb-3">
						<md-card-header>
							<div class="md-title">{{ item.job }}</div>
							<div class="md-subhead">{{ item.company }}</div>
						</md-card-header>
						<md-card-content>
							<p>
								<strong>Status:</strong>
								<span
									class="badge"
									:class="{
										accepted: item.responseStatus === 'Accepted',
										rejected: item.responseStatus === 'Rejected',
									}"
									style="margin-left: 0.2rem"
									>{{ item.responseStatus }}</span
								>
							</p>
							<p><strong>Rejection Reason:</strong> {{ item.responseStatus === 'Rejected' ? item.rejectionReason || '-' : '—' }}</p>
							<p><strong>Date:</strong> {{ item.date }}</p>
						</md-card-content>
						<md-card-actions>
							<md-button
								class="md-success flex items-center justify-center"
								style="padding: 0; height: 30px !important; min-width: 0 !important; width: 40px !important"
								@click="viewJobDetails(item.id)"
							>
								<md-icon>visibility</md-icon>
							</md-button>
						</md-card-actions>
					</md-card>
				</div>
				<p v-else class="empty-message">You haven’t responded to any invitations yet.</p>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'JobInvitationTables',
	props: ['activeTab', 'queriedData', 'pagination', 'currentSort', 'currentSortOrder', 'customSort', 'searchQuery'],
	data() {
		return {
			windowWidth: window.innerWidth,
		};
	},
	computed: {
		isDesktop() {
			return this.windowWidth >= 768;
		},
		searchQueryModel: {
			get() {
				return this.searchQuery;
			},
			set(val) {
				this.$emit('update:search-query', val);
			},
		},
	},
	mounted() {
		window.addEventListener('resize', this.updateWindowWidth);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateWindowWidth);
	},
	methods: {
		updateWindowWidth() {
			this.windowWidth = window.innerWidth;
		},
		viewJobDetails(jobId) {
			this.$router.push(`/student/jobs/micro/application/${jobId}`);
		},
	},
};
</script>

<style scoped>
.confirmation {
	font-size: 0.85rem;
	color: #374151;
	margin-top: 0.5rem;
}

.badge {
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	color: white;
	font-size: 0.75rem;
}
.badge.accepted {
	background-color: #64a151;
}
.badge.rejected {
	background-color: #e74c3c;
}

.md-icon-inline {
	vertical-align: middle;
	margin-right: 4px;
}

.empty-message {
	text-align: center;
	color: #6b7280;
	margin: 2rem 0;
	font-size: 0.95rem;
}
</style>
